import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Upload, 
  Trash2, 
  Factory, 
  Truck, 
  Search,
  Download,
  Loader2,
  CheckCircle,
  Clock,
  AlertTriangle,
  FileSpreadsheet,
  Settings,
  Table,
  ArrowRight,
  Archive,
  CalendarDays,
  User
} from 'lucide-react';
import UploadProgress from '../components/UploadProgress';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ProductionProgram = () => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ items: [], totals: {} });
  const [search, setSearch] = useState('');
  
  // Upload states for each type
  const [uploading, setUploading] = useState({
    program: false,
    addProgram: false,
    dispatch: false,
    addDispatch: false
  });
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0, phase: '' });
  
  // File states
  const [programFile, setProgramFile] = useState(null);
  const [addProgramFile, setAddProgramFile] = useState(null);
  const [dispatchFile, setDispatchFile] = useState(null);
  const [addDispatchFile, setAddDispatchFile] = useState(null);
  
  // Preview and mapping dialog states
  const [showProgramMapping, setShowProgramMapping] = useState(false);
  const [showAddProgramMapping, setShowAddProgramMapping] = useState(false);
  const [showDispatchMapping, setShowDispatchMapping] = useState(false);
  const [showAddDispatchMapping, setShowAddDispatchMapping] = useState(false);
  
  // Preview data
  const [programPreview, setProgramPreview] = useState(null);
  const [addProgramPreview, setAddProgramPreview] = useState(null);
  const [dispatchPreview, setDispatchPreview] = useState(null);
  const [addDispatchPreview, setAddDispatchPreview] = useState(null);
  
  // Loading preview states
  const [loadingPreview, setLoadingPreview] = useState({
    program: false,
    addProgram: false,
    dispatch: false,
    addDispatch: false
  });
  
  // Column mappings - using 1-based indexing like Upload.js
  const [programMapping, setProgramMapping] = useState({ headerLine: 1, itemNameColumn: 1, quantityColumn: 2 });
  const [addProgramMapping, setAddProgramMapping] = useState({ headerLine: 1, itemNameColumn: 1, quantityColumn: 2 });
  const [dispatchMapping, setDispatchMapping] = useState({ headerLine: 1, itemNameColumn: 1, quantityColumn: 2 });
  const [addDispatchMapping, setAddDispatchMapping] = useState({ headerLine: 1, itemNameColumn: 1, quantityColumn: 2 });
  
  // Clear states
  const [clearingProgram, setClearingProgram] = useState(false);
  const [clearingDispatch, setClearingDispatch] = useState(false);
  
  // Archives state
  const [showArchives, setShowArchives] = useState(false);
  const [archives, setArchives] = useState([]);
  const [loadingArchives, setLoadingArchives] = useState(false);
  const [downloadingArchive, setDownloadingArchive] = useState(null);
  
  // Production Alerts state
  const [alerts, setAlerts] = useState(null);
  const [loadingAlerts, setLoadingAlerts] = useState(false);
  const [showAlerts, setShowAlerts] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/production/summary`, { headers: getAuthHeaders() });
      setData(response.data);
    } catch (error) {
      console.error('Error fetching production data:', error);
      toast.error('Failed to fetch production data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAlerts = async () => {
    setLoadingAlerts(true);
    try {
      const response = await axios.get(`${API}/production/alerts`, { headers: getAuthHeaders() });
      setAlerts(response.data);
    } catch (error) {
      console.error('Error fetching production alerts:', error);
    } finally {
      setLoadingAlerts(false);
    }
  };

  const fetchArchives = async () => {
    setLoadingArchives(true);
    try {
      const response = await axios.get(`${API}/production/archives`, { headers: getAuthHeaders() });
      setArchives(response.data.archives || []);
    } catch (error) {
      console.error('Error fetching archives:', error);
      toast.error('Failed to fetch archives');
    } finally {
      setLoadingArchives(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchAlerts();
  }, []);

  // Generic file select handler with preview
  const handleFileSelect = async (file, type) => {
    if (!file) return;
    
    const setFile = {
      program: setProgramFile,
      addProgram: setAddProgramFile,
      dispatch: setDispatchFile,
      addDispatch: setAddDispatchFile
    }[type];
    
    const setShowMapping = {
      program: setShowProgramMapping,
      addProgram: setShowAddProgramMapping,
      dispatch: setShowDispatchMapping,
      addDispatch: setShowAddDispatchMapping
    }[type];
    
    const setPreview = {
      program: setProgramPreview,
      addProgram: setAddProgramPreview,
      dispatch: setDispatchPreview,
      addDispatch: setAddDispatchPreview
    }[type];
    
    const setMapping = {
      program: setProgramMapping,
      addProgram: setAddProgramMapping,
      dispatch: setDispatchMapping,
      addDispatch: setAddDispatchMapping
    }[type];
    
    setFile(file);
    setLoadingPreview(prev => ({ ...prev, [type]: true }));
    setShowMapping(true);
    
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API}/production/preview`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      setPreview(response.data);
      setMapping({ headerLine: 1, itemNameColumn: 1, quantityColumn: 2 });
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to preview file';
      if (errorMsg.includes('zip') || errorMsg.includes('.xls')) {
        toast.error('Please save your file as .xlsx format. The older .xls format is not supported.');
      } else {
        toast.error(errorMsg);
      }
      setShowMapping(false);
    } finally {
      setLoadingPreview(prev => ({ ...prev, [type]: false }));
    }
  };

  // Upload Program (Delete & Replace)
  const handleUploadProgram = async () => {
    if (!programFile) return;
    
    setUploading(prev => ({ ...prev, program: true }));
    setUploadProgress({ current: 0, total: 100, phase: 'Uploading production program...' });
    setShowProgramMapping(false);
    
    const formData = new FormData();
    formData.append('file', programFile);
    
    try {
      const params = new URLSearchParams({
        header_line: programMapping.headerLine,
        item_name_column: programMapping.itemNameColumn,
        quantity_column: programMapping.quantityColumn
      });
      
      const response = await axios.post(
        `${API}/production/program/upload?${params.toString()}`,
        formData,
        { 
          headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress({ current: percent, total: 100, phase: 'Uploading...' });
          }
        }
      );
      
      setUploadProgress({ current: 100, total: 100, phase: 'Complete!' });
      
      if (response.data.items_skipped > 0) {
        toast.warning(`${response.data.items_added} items uploaded, ${response.data.items_skipped} skipped`);
      } else {
        toast.success(`Production program uploaded: ${response.data.items_added} items`);
      }
      
      setProgramFile(null);
      setProgramPreview(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload production program');
    } finally {
      setUploading(prev => ({ ...prev, program: false }));
    }
  };

  // Add to Program (adds to existing quantities)
  const handleAddProgram = async () => {
    if (!addProgramFile) return;
    
    setUploading(prev => ({ ...prev, addProgram: true }));
    setUploadProgress({ current: 0, total: 100, phase: 'Adding to production program...' });
    setShowAddProgramMapping(false);
    
    const formData = new FormData();
    formData.append('file', addProgramFile);
    
    try {
      const params = new URLSearchParams({
        header_line: addProgramMapping.headerLine,
        item_name_column: addProgramMapping.itemNameColumn,
        quantity_column: addProgramMapping.quantityColumn,
        add_to_existing: 'true'
      });
      
      const response = await axios.post(
        `${API}/production/program/upload?${params.toString()}`,
        formData,
        { 
          headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress({ current: percent, total: 100, phase: 'Uploading...' });
          }
        }
      );
      
      setUploadProgress({ current: 100, total: 100, phase: 'Complete!' });
      const msg = response.data.items_updated > 0 
        ? `Added to program: ${response.data.items_added} new, ${response.data.items_updated} updated`
        : `Added to program: ${response.data.items_added} items`;
      toast.success(msg);
      
      setAddProgramFile(null);
      setAddProgramPreview(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add to production program');
    } finally {
      setUploading(prev => ({ ...prev, addProgram: false }));
    }
  };

  // Upload Dispatch (Delete & Replace)
  const handleUploadDispatch = async () => {
    if (!dispatchFile) return;
    
    setUploading(prev => ({ ...prev, dispatch: true }));
    setUploadProgress({ current: 0, total: 100, phase: 'Uploading dispatch data...' });
    setShowDispatchMapping(false);
    
    const formData = new FormData();
    formData.append('file', dispatchFile);
    
    try {
      const params = new URLSearchParams({
        header_line: dispatchMapping.headerLine,
        item_name_column: dispatchMapping.itemNameColumn,
        quantity_column: dispatchMapping.quantityColumn,
        replace_existing: 'true'
      });
      
      const response = await axios.post(
        `${API}/production/dispatch/upload?${params.toString()}`,
        formData,
        { 
          headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress({ current: percent, total: 100, phase: 'Uploading...' });
          }
        }
      );
      
      setUploadProgress({ current: 100, total: 100, phase: 'Complete!' });
      
      if (response.data.items_skipped > 0) {
        toast.warning(`${response.data.items_added + response.data.items_updated} items uploaded, ${response.data.items_skipped} skipped`);
      } else {
        toast.success(`Dispatch uploaded: ${response.data.items_added} added, ${response.data.items_updated} updated`);
      }
      
      setDispatchFile(null);
      setDispatchPreview(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload dispatch data');
    } finally {
      setUploading(prev => ({ ...prev, dispatch: false }));
    }
  };

  // Add to Dispatch (accumulative)
  const handleAddDispatch = async () => {
    if (!addDispatchFile) return;
    
    setUploading(prev => ({ ...prev, addDispatch: true }));
    setUploadProgress({ current: 0, total: 100, phase: 'Adding to dispatch data...' });
    setShowAddDispatchMapping(false);
    
    const formData = new FormData();
    formData.append('file', addDispatchFile);
    
    try {
      const params = new URLSearchParams({
        header_line: addDispatchMapping.headerLine,
        item_name_column: addDispatchMapping.itemNameColumn,
        quantity_column: addDispatchMapping.quantityColumn,
        replace_existing: 'false'
      });
      
      const response = await axios.post(
        `${API}/production/dispatch/upload?${params.toString()}`,
        formData,
        { 
          headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (progressEvent) => {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress({ current: percent, total: 100, phase: 'Uploading...' });
          }
        }
      );
      
      setUploadProgress({ current: 100, total: 100, phase: 'Complete!' });
      toast.success(`Dispatch added: ${response.data.items_added} new, ${response.data.items_updated} updated`);
      
      setAddDispatchFile(null);
      setAddDispatchPreview(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add dispatch data');
    } finally {
      setUploading(prev => ({ ...prev, addDispatch: false }));
    }
  };

  const handleClearProgram = async () => {
    setClearingProgram(true);
    try {
      const response = await axios.delete(`${API}/production/program/clear`, { headers: getAuthHeaders() });
      if (response.data.archived) {
        toast.success(`Program archived as "${response.data.archive_filename}" and cleared`);
      } else {
        toast.success(response.data.message);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear program');
    } finally {
      setClearingProgram(false);
    }
  };

  const handleClearDispatch = async () => {
    setClearingDispatch(true);
    try {
      const response = await axios.delete(`${API}/production/dispatch/clear`, { headers: getAuthHeaders() });
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear dispatch');
    } finally {
      setClearingDispatch(false);
    }
  };

  const handleDownloadArchive = async (archiveId, filename) => {
    setDownloadingArchive(archiveId);
    try {
      const response = await axios.get(
        `${API}/production/archives/${archiveId}/download`,
        { 
          headers: getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Downloaded ${filename}`);
    } catch (error) {
      toast.error('Failed to download archive');
    } finally {
      setDownloadingArchive(null);
    }
  };

  // Download templates
  const downloadTemplate = (type) => {
    let templateData, filename;
    if (type === 'program') {
      templateData = [
        ['Item Name', 'Target Quantity'],
        ['VMF Panel 1200mm', 500],
        ['VMF Panel 600mm', 300],
        ['VMF Corner Unit', 150]
      ];
      filename = `production_program_template.xlsx`;
    } else {
      templateData = [
        ['Item Name', 'Dispatched Quantity'],
        ['VMF Panel 1200mm', 350],
        ['VMF Panel 600mm', 200],
        ['VMF Corner Unit', 150]
      ];
      filename = `dispatch_template.xlsx`;
    }
    
    const ws = XLSX.utils.aoa_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Template');
    XLSX.writeFile(wb, filename);
  };

  // Export to Excel
  const exportToExcel = () => {
    if (filteredItems.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    const getUrgencyText = (urgency) => {
      switch (urgency) {
        case 'very_urgent': return 'Very Urgent';
        case 'urgent': return 'Urgent';
        case 'ok': return 'OK';
        case 'low_priority': return 'Low Priority';
        default: return '—';
      }
    };
    
    const headers = ['Item Name', 'Program Qty', 'Dispatched', 'Pending', 'Total Stock', 'Avg Sale', '% Complete', 'Urgency'];
    const rows = filteredItems.map(item => [
      item.item_name,
      item.program_qty,
      item.dispatched_qty,
      item.pending_qty,
      item.total_stock || 0,
      item.avg_sale || 0,
      item.percent_complete,
      getUrgencyText(item.urgency)
    ]);
    
    // Add totals row
    rows.push([
      'TOTAL',
      data.totals?.total_program || 0,
      data.totals?.total_dispatched || 0,
      data.totals?.total_pending || 0,
      '',
      '',
      data.totals?.overall_percent || 0,
      ''
    ]);
    
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    
    // Set column widths
    ws['!cols'] = [
      { wch: 35 },  // Item Name
      { wch: 12 },  // Program Qty
      { wch: 12 },  // Dispatched
      { wch: 12 },  // Pending
      { wch: 12 },  // Total Stock
      { wch: 12 },  // Avg Sale
      { wch: 12 },  // % Complete
      { wch: 15 }   // Urgency
    ];
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Production Program');
    
    const today = new Date().toISOString().split('T')[0];
    const filename = `production_program_${today}.xlsx`;
    XLSX.writeFile(wb, filename);
    toast.success(`Exported ${filteredItems.length} items to Excel`);
  };

  // Get mapped preview data
  const getMappedPreview = (previewData, mapping) => {
    if (!previewData) return [];
    const dataStartLine = mapping.headerLine + 1;
    return previewData.preview_rows
      .filter(row => row.line >= dataStartLine)
      .slice(0, 5)
      .map(row => ({
        itemName: row.data[mapping.itemNameColumn - 1] || '',
        quantity: row.data[mapping.quantityColumn - 1] || ''
      }));
  };

  const filteredItems = useMemo(() => {
    if (!search.trim()) return data.items;
    const query = search.toLowerCase();
    return data.items.filter(item => 
      item.item_name.toLowerCase().includes(query)
    );
  }, [data.items, search]);

  const getUrgencyBadge = (urgency) => {
    switch (urgency) {
      case 'very_urgent':
        return <Badge className="bg-red-100 text-red-700 border-0"><AlertTriangle className="w-3 h-3 mr-1" />Very Urgent</Badge>;
      case 'urgent':
        return <Badge className="bg-orange-100 text-orange-700 border-0"><Clock className="w-3 h-3 mr-1" />Urgent</Badge>;
      case 'ok':
        return <Badge className="bg-green-100 text-green-700 border-0"><CheckCircle className="w-3 h-3 mr-1" />OK</Badge>;
      case 'low_priority':
        return <Badge className="bg-slate-100 text-slate-600 border-0">Low Priority</Badge>;
      default:
        return <Badge className="bg-slate-100 text-slate-600 border-0">—</Badge>;
    }
  };

  // Reusable Column Mapping Dialog component
  const ColumnMappingDialog = ({ 
    open, 
    onOpenChange, 
    title,
    subtitle,
    loadingPreview: isLoadingPreview,
    previewData, 
    mapping, 
    setMapping, 
    onUpload, 
    uploading: isUploading,
    uploadButtonText,
    quantityLabel = "Quantity"
  }) => (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5" />
            {title}
          </DialogTitle>
        </DialogHeader>
        
        {isLoadingPreview ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            <span className="ml-3 text-slate-500">Loading file preview...</span>
          </div>
        ) : previewData && (
          <div className="space-y-6">
            {/* File Info */}
            <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
              <FileSpreadsheet className="w-8 h-8 text-blue-500" />
              <div>
                <p className="font-medium">{previewData.filename}</p>
                <p className="text-sm text-slate-500">
                  {previewData.total_rows} rows × {previewData.total_columns} columns
                </p>
              </div>
              <div className="ml-auto text-right">
                <p className="text-sm font-medium text-blue-600">Current Production Plan</p>
                <p className="text-xs text-slate-400">{subtitle}</p>
              </div>
            </div>

            {/* Mapping Configuration */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Header Line</Label>
                <Select 
                  value={mapping.headerLine.toString()} 
                  onValueChange={(v) => setMapping(prev => ({ ...prev, headerLine: parseInt(v) }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from({ length: Math.min(previewData.preview_rows.length, 15) }, (_, i) => (
                      <SelectItem key={i + 1} value={(i + 1).toString()}>Line {i + 1}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-400">Data starts from line {mapping.headerLine + 1}</p>
              </div>
              
              <div className="space-y-2">
                <Label>Item Name Column <span className="text-red-500">*</span></Label>
                <Select 
                  value={mapping.itemNameColumn.toString()} 
                  onValueChange={(v) => setMapping(prev => ({ ...prev, itemNameColumn: parseInt(v) }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {previewData.column_letters.map((letter, idx) => (
                      <SelectItem key={idx + 1} value={(idx + 1).toString()}>
                        Column {letter} ({idx + 1})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>{quantityLabel} Column <span className="text-red-500">*</span></Label>
                <Select 
                  value={mapping.quantityColumn.toString()} 
                  onValueChange={(v) => setMapping(prev => ({ ...prev, quantityColumn: parseInt(v) }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {previewData.column_letters.map((letter, idx) => (
                      <SelectItem key={idx + 1} value={(idx + 1).toString()}>
                        Column {letter} ({idx + 1})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Raw Preview */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Table className="w-4 h-4" />
                <Label>File Preview (First 15 rows)</Label>
              </div>
              <div className="border rounded-lg overflow-x-auto max-h-48">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100 sticky top-0">
                    <tr>
                      <th className="px-2 py-1.5 text-left font-medium text-slate-600 w-16">Line</th>
                      {previewData.column_letters.map((letter, idx) => (
                        <th key={letter} className={`px-2 py-1.5 text-left font-medium min-w-[100px] ${
                          idx + 1 === mapping.itemNameColumn ? 'bg-green-100 text-green-700' :
                          idx + 1 === mapping.quantityColumn ? 'bg-purple-100 text-purple-700' :
                          'text-slate-600'
                        }`}>
                          {letter}
                          {idx + 1 === mapping.itemNameColumn && <span className="ml-1">(Name)</span>}
                          {idx + 1 === mapping.quantityColumn && <span className="ml-1">(Qty)</span>}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.preview_rows.slice(0, 15).map((row) => (
                      <tr 
                        key={row.line} 
                        className={`border-t ${
                          row.line === mapping.headerLine ? 'bg-yellow-50 font-medium' : 
                          row.line > mapping.headerLine ? 'bg-white' : 'bg-slate-50 text-slate-400'
                        }`}
                      >
                        <td className="px-2 py-1.5 font-mono">
                          {row.line}
                          {row.line === mapping.headerLine && (
                            <Badge variant="secondary" className="ml-1 text-[10px] py-0">Header</Badge>
                          )}
                        </td>
                        {row.data.map((cell, idx) => (
                          <td key={idx} className={`px-2 py-1.5 truncate max-w-[150px] ${
                            idx + 1 === mapping.itemNameColumn ? 'bg-green-50' :
                            idx + 1 === mapping.quantityColumn ? 'bg-purple-50' : ''
                          }`}>
                            {cell || <span className="text-slate-300">—</span>}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Mapped Preview */}
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <ArrowRight className="w-4 h-4" />
                <Label>Mapped Data Preview</Label>
              </div>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-900 text-white">
                    <tr>
                      <th className="px-3 py-2 text-left">Item Name</th>
                      <th className="px-3 py-2 text-right">{quantityLabel}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getMappedPreview(previewData, mapping).length > 0 ? (
                      getMappedPreview(previewData, mapping).map((row, idx) => (
                        <tr key={idx} className="border-t">
                          <td className="px-3 py-2 font-medium">{row.itemName || '—'}</td>
                          <td className="px-3 py-2 text-right font-mono">{row.quantity || '—'}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={2} className="px-3 py-4 text-center text-slate-400">
                          No data rows found. Check your header line setting.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-slate-500">
                Showing first 5 data rows (starting from line {mapping.headerLine + 1})
              </p>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onUpload} disabled={isUploading}>
            {isUploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                {uploadButtonText}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  // Reusable Upload Card component
  const UploadCard = ({
    title,
    description,
    icon: Icon,
    iconColor,
    file,
    onFileSelect,
    onUpload,
    isUploading,
    templateType,
    testId,
    uploadButtonText,
    previewData
  }) => (
    <Card data-testid={testId}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
          {title}
        </CardTitle>
        <CardDescription className="text-xs">{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div
          className="border-2 border-dashed border-slate-200 rounded-lg p-4 text-center cursor-pointer hover:border-slate-300 hover:bg-slate-50 transition-colors"
          onClick={() => document.getElementById(`${testId}-input`).click()}
        >
          <input
            id={`${testId}-input`}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={(e) => onFileSelect(e.target.files[0])}
          />
          {file ? (
            <div className="flex items-center justify-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <div className="text-left">
                <p className="font-medium text-sm text-slate-900">{file.name}</p>
                <p className="text-xs text-slate-500">{(file.size / 1024).toFixed(1)} KB</p>
              </div>
            </div>
          ) : (
            <>
              <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
              <p className="text-sm text-slate-600">Drop Excel file or click</p>
            </>
          )}
        </div>

        {file && (
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={() => onFileSelect(file)}
          >
            <Settings className="w-4 h-4 mr-2" />
            Configure Mapping
          </Button>
        )}

        {isUploading ? (
          <UploadProgress 
            current={uploadProgress.current} 
            total={uploadProgress.total} 
            phase={uploadProgress.phase} 
          />
        ) : (
          <div className="flex gap-2">
            <Button
              size="sm"
              onClick={() => file && previewData ? onUpload() : onFileSelect(file)}
              disabled={!file || isUploading}
              className="flex-1"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploadButtonText}
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => downloadTemplate(templateType)}
              title="Download Template"
            >
              <Download className="w-4 h-4" />
            </Button>
          </div>
        )}

        <p className="text-xs text-slate-400">Required: Item Name, {templateType === 'program' ? 'Target Quantity' : 'Dispatched Quantity'}</p>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6" data-testid="production-program-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Production Program
          </h1>
          <p className="text-slate-500 mt-1">Track production targets vs actual dispatch</p>
        </div>
        
        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          <Button 
            onClick={() => { setShowArchives(true); fetchArchives(); }} 
            variant="outline"
            data-testid="view-archives-btn"
          >
            <Archive className="w-4 h-4 mr-2" />
            Archives
          </Button>
          <Button onClick={exportToExcel} variant="outline" data-testid="export-production-btn">
            <Download className="w-4 h-4 mr-2" />
            Export Excel
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Program</p>
                <p className="text-2xl font-bold text-slate-900">{(data.totals?.total_program || 0).toLocaleString()}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-xl">
                <Factory className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Dispatched</p>
                <p className="text-2xl font-bold text-green-600">{(data.totals?.total_dispatched || 0).toLocaleString()}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-xl">
                <Truck className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pending</p>
                <p className="text-2xl font-bold text-orange-600">{(data.totals?.total_pending || 0).toLocaleString()}</p>
              </div>
              <div className="bg-orange-100 p-3 rounded-xl">
                <Clock className="w-6 h-6 text-orange-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Completion</p>
                <p className="text-2xl font-bold text-purple-600">{data.totals?.overall_percent || 0}%</p>
              </div>
              <div className="bg-purple-100 p-3 rounded-xl">
                <CheckCircle className="w-6 h-6 text-purple-600" />
              </div>
            </div>
            <Progress value={data.totals?.overall_percent || 0} className="mt-2 h-2" />
          </CardContent>
        </Card>
      </div>

      {/* Production Alerts Section */}
      {alerts && alerts.total_alerts > 0 && (
        <Card className="border-orange-200 bg-orange-50/30">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg flex items-center gap-2 text-orange-800">
                <AlertTriangle className="w-5 h-5" />
                Production Alerts ({alerts.total_alerts})
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setShowAlerts(!showAlerts)}
                className="text-orange-700"
              >
                {showAlerts ? 'Hide' : 'Show'}
              </Button>
            </div>
            {alerts.selected_categories?.length > 0 && (
              <p className="text-sm text-orange-600 mt-1">
                Monitoring: {alerts.selected_categories.map(c => c.name).join(', ')}
              </p>
            )}
          </CardHeader>
          
          {showAlerts && (
            <CardContent className="space-y-6">
              {/* Table 1: Not in Production Program */}
              {alerts.not_in_program?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-red-700 mb-2 flex items-center gap-2">
                    <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded text-xs">{alerts.not_in_program.length}</span>
                    Items NOT in Production Program
                  </h4>
                  <p className="text-xs text-slate-500 mb-2">These low-stock items have no production planned yet</p>
                  <div className="border rounded-lg overflow-hidden">
                    <div className="overflow-x-auto max-h-64">
                      <table className="w-full text-sm">
                        <thead className="bg-red-50 sticky top-0">
                          <tr>
                            <th className="text-left px-3 py-2 font-medium text-red-800">Item</th>
                            <th className="text-left px-3 py-2 font-medium text-red-800">Category</th>
                            <th className="text-right px-3 py-2 font-medium text-red-800">Stock</th>
                            <th className="text-right px-3 py-2 font-medium text-red-800">Buffer</th>
                            <th className="text-right px-3 py-2 font-medium text-red-800">Shortage</th>
                            <th className="text-right px-3 py-2 font-medium text-red-800">Reorder Qty</th>
                          </tr>
                        </thead>
                        <tbody>
                          {alerts.not_in_program.map((item, idx) => (
                            <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-red-50/30'}>
                              <td className="px-3 py-2 font-medium text-slate-900">{item.item_name}</td>
                              <td className="px-3 py-2 text-slate-600">{item.category_name}</td>
                              <td className="px-3 py-2 text-right text-slate-700">{item.total_stock.toLocaleString()}</td>
                              <td className="px-3 py-2 text-right text-slate-700">{item.buffer_level.toLocaleString()}</td>
                              <td className="px-3 py-2 text-right text-red-600 font-medium">{item.shortage.toLocaleString()}</td>
                              <td className="px-3 py-2 text-right text-red-700 font-bold">{item.reorder_qty.toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Table 2: Insufficient Planned Quantity */}
              {alerts.insufficient_planned?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-amber-700 mb-2 flex items-center gap-2">
                    <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded text-xs">{alerts.insufficient_planned.length}</span>
                    Insufficient Planned Quantity
                  </h4>
                  <p className="text-xs text-slate-500 mb-2">Planned qty is less than 50% of recommended reorder qty</p>
                  <div className="border rounded-lg overflow-hidden">
                    <div className="overflow-x-auto max-h-64">
                      <table className="w-full text-sm">
                        <thead className="bg-amber-50 sticky top-0">
                          <tr>
                            <th className="text-left px-3 py-2 font-medium text-amber-800">Item</th>
                            <th className="text-left px-3 py-2 font-medium text-amber-800">Category</th>
                            <th className="text-right px-3 py-2 font-medium text-amber-800">Stock</th>
                            <th className="text-right px-3 py-2 font-medium text-amber-800">Planned</th>
                            <th className="text-right px-3 py-2 font-medium text-amber-800">Reorder Qty</th>
                            <th className="text-left px-3 py-2 font-medium text-amber-800">Issue</th>
                          </tr>
                        </thead>
                        <tbody>
                          {alerts.insufficient_planned.map((item, idx) => (
                            <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-amber-50/30'}>
                              <td className="px-3 py-2 font-medium text-slate-900">{item.item_name}</td>
                              <td className="px-3 py-2 text-slate-600">{item.category_name}</td>
                              <td className="px-3 py-2 text-right text-slate-700">{item.total_stock.toLocaleString()}</td>
                              <td className="px-3 py-2 text-right text-amber-600 font-medium">{item.program_qty.toLocaleString()}</td>
                              <td className="px-3 py-2 text-right text-amber-700 font-bold">{item.reorder_qty.toLocaleString()}</td>
                              <td className="px-3 py-2 text-xs text-amber-600">{item.planned_vs_reorder}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Table 3: Fully Dispatched but Still in Reorder */}
              {alerts.fully_dispatched?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-blue-700 mb-2 flex items-center gap-2">
                    <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs">{alerts.fully_dispatched.length}</span>
                    Fully Dispatched - Need More Production
                  </h4>
                  <p className="text-xs text-slate-500 mb-2">Production complete but item still needs restocking</p>
                  <div className="border rounded-lg overflow-hidden">
                    <div className="overflow-x-auto max-h-64">
                      <table className="w-full text-sm">
                        <thead className="bg-blue-50 sticky top-0">
                          <tr>
                            <th className="text-left px-3 py-2 font-medium text-blue-800">Item</th>
                            <th className="text-left px-3 py-2 font-medium text-blue-800">Category</th>
                            <th className="text-right px-3 py-2 font-medium text-blue-800">Stock</th>
                            <th className="text-right px-3 py-2 font-medium text-blue-800">Planned</th>
                            <th className="text-right px-3 py-2 font-medium text-blue-800">Dispatched</th>
                            <th className="text-right px-3 py-2 font-medium text-blue-800">Additional Needed</th>
                          </tr>
                        </thead>
                        <tbody>
                          {alerts.fully_dispatched.map((item, idx) => (
                            <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-blue-50/30'}>
                              <td className="px-3 py-2 font-medium text-slate-900">{item.item_name}</td>
                              <td className="px-3 py-2 text-slate-600">{item.category_name}</td>
                              <td className="px-3 py-2 text-right text-slate-700">{item.total_stock.toLocaleString()}</td>
                              <td className="px-3 py-2 text-right text-slate-700">{item.program_qty.toLocaleString()}</td>
                              <td className="px-3 py-2 text-right text-green-600 font-medium">{item.dispatch_qty.toLocaleString()}</td>
                              <td className="px-3 py-2 text-right text-blue-700 font-bold">{item.reorder_qty.toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          )}
        </Card>
      )}

      {/* No alerts configured message */}
      {alerts && alerts.message && (
        <Card className="border-slate-200 bg-slate-50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 text-slate-600">
              <Settings className="w-5 h-5" />
              <span>{alerts.message}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Loading alerts */}
      {loadingAlerts && !alerts && (
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span>Loading production alerts...</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upload Cards - 2x2 Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Upload Program */}
        <UploadCard
          title="Upload Program"
          description="Replace current production program"
          icon={Factory}
          iconColor="text-blue-500"
          file={programFile}
          onFileSelect={(f) => handleFileSelect(f, 'program')}
          onUpload={handleUploadProgram}
          isUploading={uploading.program}
          templateType="program"
          testId="upload-program-card"
          uploadButtonText="Upload"
          previewData={programPreview}
        />

        {/* Add to Program */}
        <UploadCard
          title="Add to Program"
          description="Replace with new items"
          icon={Factory}
          iconColor="text-blue-400"
          file={addProgramFile}
          onFileSelect={(f) => handleFileSelect(f, 'addProgram')}
          onUpload={handleAddProgram}
          isUploading={uploading.addProgram}
          templateType="program"
          testId="add-program-card"
          uploadButtonText="Add"
          previewData={addProgramPreview}
        />

        {/* Upload Dispatch */}
        <UploadCard
          title="Upload Dispatch"
          description="Replace current dispatch data"
          icon={Truck}
          iconColor="text-green-500"
          file={dispatchFile}
          onFileSelect={(f) => handleFileSelect(f, 'dispatch')}
          onUpload={handleUploadDispatch}
          isUploading={uploading.dispatch}
          templateType="dispatch"
          testId="upload-dispatch-card"
          uploadButtonText="Upload"
          previewData={dispatchPreview}
        />

        {/* Add to Dispatch */}
        <UploadCard
          title="Add to Dispatch"
          description="Add quantities to existing dispatch"
          icon={Truck}
          iconColor="text-green-400"
          file={addDispatchFile}
          onFileSelect={(f) => handleFileSelect(f, 'addDispatch')}
          onUpload={handleAddDispatch}
          isUploading={uploading.addDispatch}
          templateType="dispatch"
          testId="add-dispatch-card"
          uploadButtonText="Add"
          previewData={addDispatchPreview}
        />
      </div>

      {/* Clear Actions */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-600">
              Clear current production data
            </div>
            <div className="flex gap-2">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50">
                    <Archive className="w-4 h-4 mr-2" />
                    Clear & Archive Program
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Archive & Clear Production Program?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will save a snapshot of the current production program vs dispatch to archives (downloadable as Excel), then delete the current program and dispatch data. This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleClearProgram} className="bg-red-600 hover:bg-red-700">
                      {clearingProgram ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Archive className="w-4 h-4 mr-2" />}
                      Archive & Clear
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>

              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" className="text-orange-600 border-orange-200 hover:bg-orange-50">
                    <Trash2 className="w-4 h-4 mr-2" />
                    Clear Dispatch Only
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Clear Dispatch Data?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will delete all dispatch data but keep the production program intact. This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleClearDispatch} className="bg-orange-600 hover:bg-orange-700">
                      {clearingDispatch ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Clear Dispatch
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">
              Current Production Plan
              <Badge variant="secondary" className="ml-2">{filteredItems.length} items</Badge>
            </CardTitle>
            
            {/* Search */}
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder="Search items..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="production-search"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Factory className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>No production data</p>
              <p className="text-sm mt-2">Upload a production program to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 font-medium text-slate-700">Item Name</th>
                    <th className="text-right py-3 px-4 font-medium text-slate-700">Program Qty</th>
                    <th className="text-right py-3 px-4 font-medium text-slate-700">Dispatched</th>
                    <th className="text-right py-3 px-4 font-medium text-slate-700">Pending</th>
                    <th className="text-right py-3 px-4 font-medium text-slate-700">Total Stock</th>
                    <th className="text-right py-3 px-4 font-medium text-slate-700">Avg Sale</th>
                    <th className="text-center py-3 px-4 font-medium text-slate-700">Progress</th>
                    <th className="text-center py-3 px-4 font-medium text-slate-700">Urgency</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((item) => (
                    <tr key={item.item_name} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4 font-medium">{item.item_name}</td>
                      <td className="py-3 px-4 text-right text-blue-600 font-medium">
                        {item.program_qty.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right text-green-600 font-medium">
                        {item.dispatched_qty.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right text-orange-600 font-medium">
                        {item.pending_qty.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right text-purple-600 font-medium">
                        {(item.total_stock || 0).toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-right text-slate-600">
                        {(item.avg_sale || 0).toLocaleString()}
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <Progress value={item.percent_complete} className="h-2 flex-1" />
                          <span className="text-sm text-slate-600 w-12 text-right">{item.percent_complete}%</span>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-center">
                        {getUrgencyBadge(item.urgency)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50 font-bold">
                    <td className="py-3 px-4">TOTAL</td>
                    <td className="py-3 px-4 text-right text-blue-600">
                      {(data.totals?.total_program || 0).toLocaleString()}
                    </td>
                    <td className="py-3 px-4 text-right text-green-600">
                      {(data.totals?.total_dispatched || 0).toLocaleString()}
                    </td>
                    <td className="py-3 px-4 text-right text-orange-600">
                      {(data.totals?.total_pending || 0).toLocaleString()}
                    </td>
                    <td className="py-3 px-4 text-right">—</td>
                    <td className="py-3 px-4 text-right">—</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Progress value={data.totals?.overall_percent || 0} className="h-2 flex-1" />
                        <span className="text-sm w-12 text-right">{data.totals?.overall_percent || 0}%</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center">—</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Archives Modal */}
      <Dialog open={showArchives} onOpenChange={setShowArchives}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Archive className="w-5 h-5" />
              Production Archives
            </DialogTitle>
          </DialogHeader>
          
          {loadingArchives ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : archives.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Archive className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>No archived programs</p>
              <p className="text-sm mt-2">Archives are created when you clear a production program</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {archives.map((archive) => (
                <div 
                  key={archive.id} 
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-lg border"
                >
                  <div className="flex items-center gap-4">
                    <div className="bg-white p-2 rounded-lg border">
                      <FileSpreadsheet className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium text-slate-900">{archive.filename}</p>
                      <div className="flex items-center gap-4 text-xs text-slate-500 mt-1">
                        <span className="flex items-center gap-1">
                          <CalendarDays className="w-3 h-3" />
                          {new Date(archive.archived_at).toLocaleDateString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {archive.archived_by_name}
                        </span>
                        <span>{archive.item_count} items</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs mt-1">
                        <span className="text-blue-600">Program: {archive.totals?.total_program?.toLocaleString() || 0}</span>
                        <span className="text-green-600">Dispatched: {archive.totals?.total_dispatched?.toLocaleString() || 0}</span>
                        <span className="text-purple-600">{archive.totals?.overall_percent || 0}%</span>
                      </div>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDownloadArchive(archive.id, archive.filename)}
                    disabled={downloadingArchive === archive.id}
                  >
                    {downloadingArchive === archive.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              ))}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowArchives(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Column Mapping Dialogs */}
      <ColumnMappingDialog
        open={showProgramMapping}
        onOpenChange={setShowProgramMapping}
        title="Upload Production Program"
        subtitle="Replaces existing data"
        loadingPreview={loadingPreview.program}
        previewData={programPreview}
        mapping={programMapping}
        setMapping={setProgramMapping}
        onUpload={handleUploadProgram}
        uploading={uploading.program}
        uploadButtonText="Upload & Replace"
        quantityLabel="Target Quantity"
      />

      <ColumnMappingDialog
        open={showAddProgramMapping}
        onOpenChange={setShowAddProgramMapping}
        title="Add to Production Program"
        subtitle="Replaces existing data"
        loadingPreview={loadingPreview.addProgram}
        previewData={addProgramPreview}
        mapping={addProgramMapping}
        setMapping={setAddProgramMapping}
        onUpload={handleAddProgram}
        uploading={uploading.addProgram}
        uploadButtonText="Upload & Replace"
        quantityLabel="Target Quantity"
      />

      <ColumnMappingDialog
        open={showDispatchMapping}
        onOpenChange={setShowDispatchMapping}
        title="Upload Dispatch Data"
        subtitle="Replaces existing data"
        loadingPreview={loadingPreview.dispatch}
        previewData={dispatchPreview}
        mapping={dispatchMapping}
        setMapping={setDispatchMapping}
        onUpload={handleUploadDispatch}
        uploading={uploading.dispatch}
        uploadButtonText="Upload & Replace"
        quantityLabel="Dispatched Quantity"
      />

      <ColumnMappingDialog
        open={showAddDispatchMapping}
        onOpenChange={setShowAddDispatchMapping}
        title="Add to Dispatch Data"
        subtitle="Adds to existing quantities"
        loadingPreview={loadingPreview.addDispatch}
        previewData={addDispatchPreview}
        mapping={addDispatchMapping}
        setMapping={setAddDispatchMapping}
        onUpload={handleAddDispatch}
        uploading={uploading.addDispatch}
        uploadButtonText="Add to Existing"
        quantityLabel="Dispatched Quantity"
      />
    </div>
  );
};

export default ProductionProgram;
